/**
 * Diffs2Detail Page - Detail view for external API diffs
 * Route: /diffs2/:slug/:period
 * Fetches diff data from external API using slug + period
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/store/store';
import {
  incrementApiRequestCount,
  updateExternalDiffInList,
} from '@/store/externalDiffsSlice';
import { fetchDiffs, toggleDiffHighlight, updateDiffFields, setDiffPublished, resetDiffQACheck, warmupManagementApi } from '@/utils/externalDiffsAPI';
import { transformExternalDiffs } from '@/utils/externalDiffsTransformer';
import type { Diff } from '@/utils/externalDiffsTransformer';
import { supabase } from '@/integrations/supabase/client';
import { DiffDetailViewer } from '@/components/diffs/DiffDetailViewer';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Star,
  Check,
  X,
  Sparkles,
  Eye,
  EyeOff,
  RotateCcw,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface RouteParams {
  slug: string;
  period: string;
}

// Warm up the management API edge function on module load (fire-and-forget)
warmupManagementApi();

const Diffs2Detail = () => {
  const { slug, period } = useParams<keyof RouteParams>() as RouteParams;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();

  // Local state
  const [loading, setLoading] = useState(true);
  const [diff, setDiff] = useState<Diff | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [showRedoQADialog, setShowRedoQADialog] = useState(false);
  const [showInvalid, setShowInvalid] = useState(() => {
    const saved = localStorage.getItem('diffs2_show_invalid');
    return saved === 'true';
  });

  // Diff field editing state (independent per field so both can be edited simultaneously)
  const [pendingTitle, setPendingTitle] = useState<string | null>(null);
  const [pendingDescription, setPendingDescription] = useState<string | null>(null);
  const [aiGeneratingField, setAiGeneratingField] = useState<'title' | 'description' | null>(null);
  const [pricingPageUrl, setPricingPageUrl] = useState<string | null>(null);

  const hasPendingFieldEdits = pendingTitle !== null || pendingDescription !== null;

  // Persist showInvalid to localStorage
  useEffect(() => {
    localStorage.setItem('diffs2_show_invalid', String(showInvalid));
  }, [showInvalid]);

  // Navigation state
  const [searchParams] = useSearchParams();
  const navigationIndex = parseInt(searchParams.get('index') || '-1', 10);
  const navigationList = useSelector((state: RootState) =>
    state.externalDiffs.navigationList
  );
  const currentFilters = useSelector((state: RootState) =>
    state.externalDiffs.currentFilters
  );
  const cachedDiffs = useSelector((state: RootState) =>
    state.externalDiffs.listCache?.data.diffs || []
  );

  /**
   * Helper function to check if diff has valid events
   */
  const hasValidEvents = (diff: Diff): boolean => {
    if (!diff.changes || diff.changes.length === 0) return true;
    return diff.changes.some(event => !event.invalid);
  };

  /**
   * Filter navigation list to exclude all-invalid diffs and apply published filter
   */
  const filteredNavigationList = useMemo(() => {
    if (!navigationList) return navigationList;

    return navigationList.filter(diffId => {
      const diff = cachedDiffs.find(d => d.id === diffId);
      if (!diff) return true;

      // Apply show-invalid filter
      if (!showInvalid && !hasValidEvents(diff)) return false;

      // Apply published filter
      const publishedFilter = currentFilters.published_filter;
      if (publishedFilter === 'published' && !diff.published) return false;
      if (publishedFilter === 'unpublished' && diff.published) return false;

      return true;
    });
  }, [navigationList, cachedDiffs, showInvalid, currentFilters.published_filter]);

  /**
   * Navigation helpers
   */
  const hasNavigation = filteredNavigationList && filteredNavigationList.length > 0 && navigationIndex >= 0;

  // Find current diff in navigation list, fallback to navigationIndex from URL
  const currentDiffIndex = hasNavigation && diff?.id
    ? (filteredNavigationList.findIndex(id => id === diff.id) !== -1
        ? filteredNavigationList.findIndex(id => id === diff.id)
        : navigationIndex)
    : -1;

  console.log('[Diffs2Detail] Navigation state:', {
    hasNavigation,
    navigationListLength: filteredNavigationList?.length,
    navigationIndex,
    diffId: diff?.id,
    diffSlug: diff?.slug,
    currentDiffIndex,
    foundInList: diff?.id ? filteredNavigationList?.findIndex(id => id === diff.id) : -1,
  });

  const hasPrev = hasNavigation && currentDiffIndex > 0;
  const hasNext = hasNavigation && currentDiffIndex < filteredNavigationList.length - 1;

  const prevDiffId = hasPrev ? filteredNavigationList[currentDiffIndex - 1] : null;
  const nextDiffId = hasNext ? filteredNavigationList[currentDiffIndex + 1] : null;

  // Get prev/next diff metadata from cache
  const prevDiff = prevDiffId ? cachedDiffs.find(d => d.id === prevDiffId) : null;
  const nextDiff = nextDiffId ? cachedDiffs.find(d => d.id === nextDiffId) : null;

  // Navigation with dirty check (includes pending title/description edits)
  const attemptNavigation = (targetUrl: string) => {
    if (isDirty || hasPendingFieldEdits) {
      setPendingNavigation(targetUrl);
      setShowUnsavedDialog(true);
    } else {
      navigate(targetUrl);
    }
  };

  const navigateToPrev = () => {
    if (prevDiff) {
      attemptNavigation(`/diffs2/${prevDiff.slug}/${prevDiff.period}?index=${currentDiffIndex - 1}`);
    }
  };

  const navigateToNext = () => {
    if (nextDiff) {
      attemptNavigation(`/diffs2/${nextDiff.slug}/${nextDiff.period}?index=${currentDiffIndex + 1}`);
    }
  };

  const handleBackToList = () => {
    const params = new URLSearchParams();
    if (currentFilters.period) params.set('period', currentFilters.period);
    if (currentFilters.slug) params.set('slug', currentFilters.slug);
    if (currentFilters.event_types?.length) {
      params.set('event_types', currentFilters.event_types.join(','));
    }

    attemptNavigation(`/diffs2?${params.toString()}`);
  };

  const handleConfirmNavigate = () => {
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
    setShowUnsavedDialog(false);
  };

  const handleCancelNavigate = () => {
    setPendingNavigation(null);
    setShowUnsavedDialog(false);
  };

  /**
   * Fetch pricing page URL in the background (non-blocking)
   */
  const fetchPricingPageUrl = async (diffSlug: string) => {
    const dotIndex = diffSlug.indexOf('.');
    const companySlugPart = dotIndex > 0 ? diffSlug.substring(0, dotIndex) : diffSlug;
    const subslugPart = dotIndex > 0 ? diffSlug.substring(dotIndex + 1) : null;

    if (!companySlugPart) return;

    try {
      const VAULT_API_BASE = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL;
      const VAULT_API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY;

      const companyResponse = await fetch(`${VAULT_API_BASE}/companies/slug/${companySlugPart}`, {
        headers: { 'X-API-Key': VAULT_API_KEY, 'Content-Type': 'application/json' },
      });

      if (companyResponse.ok) {
        const companyJson = await companyResponse.json();
        const companyData = companyJson.data || companyJson;

        if (companyData?.id) {
          const pagesResponse = await fetch(`${VAULT_API_BASE}/pages?company_id=${companyData.id}`, {
            headers: { 'X-API-Key': VAULT_API_KEY, 'Content-Type': 'application/json' },
          });

          if (pagesResponse.ok) {
            const pagesJson = await pagesResponse.json();
            const pages = pagesJson.pages || [];
            const matchedPage = subslugPart
              ? pages.find((p: any) => p.subslug === subslugPart)
              : pages[0];
            if (matchedPage?.pricing_page_url) {
              setPricingPageUrl(matchedPage.pricing_page_url);
            }
          }
        }
      }
    } catch (err) {
      console.warn('[Diffs2Detail] Failed to fetch pricing page URL:', err);
    }
  };

  /**
   * Fetch diff by slug and period
   */
  const loadDiff = async () => {
    if (!slug || !period) {
      setError('Missing slug or period parameter');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setPricingPageUrl(null);

      // Always fetch the specific company's diffs using slug
      const response = await fetchDiffs({
        slug,
        period,
        limit: 100,
      });

      // Track API request
      dispatch(incrementApiRequestCount({
        endpoint: `/api/diffs/${slug}`,
        params: { period },
      }));

      const transformedDiffs = transformExternalDiffs(response.data);

      if (transformedDiffs.length > 0) {
        // Find the diff matching our period (there may be multiple)
        const foundDiff = transformedDiffs.find(d => d.period === period) || transformedDiffs[0];
        console.log('[Diffs2Detail] Loaded diff:', {
          id: foundDiff.id,
          slug: foundDiff.slug,
          primary_original_url: foundDiff.primary_original_url,
          secondary_original_url: foundDiff.secondary_original_url,
          changes_count: foundDiff.changes?.length,
          hasNavigation: !!navigationList,
        });
        setDiff(foundDiff);
        setLoading(false);

        // Fetch pricing page URL in the background (non-blocking)
        fetchPricingPageUrl(foundDiff.slug);

        // Navigation is populated when coming from the list page.
        // For direct visits, prev/next is unavailable but page loads faster.
      } else {
        setError(`No diff found for ${slug} in period ${period}`);
        setLoading(false);
      }
    } catch (err: any) {
      console.error('[Diffs2Detail] Error loading diff:', err);
      setError(err.message || 'Failed to load diff');
      setLoading(false);
      toast({
        title: 'Error loading diff',
        description: err.message || 'Failed to fetch data from external API',
        variant: 'destructive',
      });
    }
  };

  // Background navigation build removed — the navigation list is populated
  // when the user comes from the list page. For direct visits, prev/next
  // is not available but the page loads much faster (saves a ~2s+ API call
  // that fetched 500 full diffs just for navigation IDs).


  /**
   * Effect: Load diff on mount or when params change
   */
  useEffect(() => {
    window.scrollTo(0, 0);
    setPendingTitle(null);
    setPendingDescription(null);
    loadDiff();
  }, [slug, period]);

  /**
   * Effect: Keyboard shortcuts for navigation (with dirty check)
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input or if unsaved dialog is open
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        showUnsavedDialog
      ) {
        return;
      }

      if (e.key === 'ArrowLeft' && hasPrev) {
        e.preventDefault();
        navigateToPrev();
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        navigateToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasPrev, hasNext, prevDiff, nextDiff, isDirty, showUnsavedDialog]);

  /**
   * Handle save from DiffDetailViewer
   */
  const handleSave = async () => {
    // Reload diff data after save
    await loadDiff();
    setIsDirty(false);
  };

  /**
   * Handle save and navigate to next diff
   */
  const handleSaveAndNext = () => {
    if (nextDiff) {
      navigate(`/diffs2/${nextDiff.slug}/${nextDiff.period}?index=${currentDiffIndex + 1}`);
    }
  };

  /**
   * Handle save, publish, and navigate to next diff
   */
  const handleSavePublishAndNext = async () => {
    await handleTogglePublished();
    if (nextDiff) {
      navigate(`/diffs2/${nextDiff.slug}/${nextDiff.period}?index=${currentDiffIndex + 1}`);
    }
  };

  /**
   * Handle event invalidation
   * Note: Auto-navigation removed - users now see empty state with "Show Invalid Events" button
   */
  const handleInvalidateEvent = async (_eventId: string, _hasValidEventsRemaining: boolean) => {
    // No auto-navigation - let users see the empty state and decide what to do
  };

  /**
   * Handle opening the actual pricing page URL
   */
  const handleOpenPricingPage = () => {
    if (pricingPageUrl) {
      window.open(pricingPageUrl, '_blank', 'noopener,noreferrer');
    }
  };

  /**
   * Handle opening company page in external website
   */
  const handleOpenWebsite = () => {
    const companySlug = diff?.page?.company?.slug || slug;
    const externalUrl = `https://pulse.pricingsaas.com/companies/${companySlug}/diffs/${period}`;
    window.open(externalUrl, '_blank', 'noopener,noreferrer');
  };

  /**
   * Toggle highlight flag for this diff
   */
  const handleToggleHighlight = async () => {
    if (!diff) return;

    const newHighlightValue = !diff.highlight;

    // Optimistic update
    const updatedDiff = { ...diff, highlight: newHighlightValue };
    setDiff(updatedDiff);
    dispatch(updateExternalDiffInList(updatedDiff));

    try {
      await toggleDiffHighlight(diff.id, newHighlightValue);

      toast({
        title: newHighlightValue ? 'Diff highlighted' : 'Highlight removed',
        description: `${diff.slug} has been ${newHighlightValue ? 'highlighted' : 'unhighlighted'}`,
      });
    } catch (error: any) {
      // Revert optimistic update on error
      setDiff(diff);
      dispatch(updateExternalDiffInList(diff));

      toast({
        title: 'Error updating highlight',
        description: error.message || 'Failed to update highlight status',
        variant: 'destructive',
      });
    }
  };

  /**
   * Toggle published flag for this diff
   */
  const handleTogglePublished = async () => {
    if (!diff) return;

    const newPublishedValue = !diff.published;

    // Optimistic update
    const updatedDiff = { ...diff, published: newPublishedValue };
    setDiff(updatedDiff);
    dispatch(updateExternalDiffInList(updatedDiff));

    try {
      await setDiffPublished(diff.id, newPublishedValue);

      toast({
        title: newPublishedValue ? 'Diff published' : 'Diff unpublished',
        description: `${diff.slug} is now ${newPublishedValue ? 'published' : 'a draft'}`,
      });
    } catch (error: any) {
      // Revert optimistic update on error
      setDiff(diff);
      dispatch(updateExternalDiffInList(diff));

      toast({
        title: 'Error updating published status',
        description: error.message || 'Failed to update published status',
        variant: 'destructive',
      });
    }
  };

  /**
   * Reset QA check status (send back to QA pending)
   */
  const handleRedoQA = async () => {
    if (!diff) return;

    const previousDiff = diff;
    const updatedDiff = { ...diff, qa_check: null };
    setDiff(updatedDiff);
    dispatch(updateExternalDiffInList(updatedDiff));

    try {
      await resetDiffQACheck(diff.id);

      toast({
        title: 'QA status reset',
        description: `${diff.slug} has been sent back to QA pending`,
      });
    } catch (error: any) {
      setDiff(previousDiff);
      dispatch(updateExternalDiffInList(previousDiff));

      toast({
        title: 'Error resetting QA status',
        description: error.message || 'Failed to reset QA check',
        variant: 'destructive',
      });
    }
  };

  /**
   * Diff field editing handlers (independent per field)
   */
  const handleStartTitleEdit = () => {
    if (pendingTitle === null) setPendingTitle(diff?.hook || '');
  };

  const handleStartDescriptionEdit = () => {
    if (pendingDescription === null) setPendingDescription(diff?.description || '');
  };

  const handleSaveField = async (field: 'title' | 'description') => {
    if (!diff) return;
    const value = field === 'title' ? pendingTitle : pendingDescription;
    if (value === null) return;

    try {
      await updateDiffFields(diff.id, { [field]: value });

      const internalField = field === 'title' ? 'hook' : 'description';
      const updatedDiff = { ...diff, [internalField]: value };
      setDiff(updatedDiff);
      dispatch(updateExternalDiffInList(updatedDiff));

      if (field === 'title') setPendingTitle(null);
      else setPendingDescription(null);

      toast({ title: `${field === 'title' ? 'Title' : 'Description'} updated` });
    } catch (error: any) {
      toast({
        title: `Error updating ${field}`,
        description: error.message || 'Failed to save',
        variant: 'destructive',
      });
    }
  };

  const handleCancelField = (field: 'title' | 'description') => {
    if (field === 'title') setPendingTitle(null);
    else setPendingDescription(null);
  };

  /** Save all pending field edits (used by unsaved dialog) */
  const savePendingFields = async () => {
    if (!diff) return;
    const fields: Record<string, string> = {};
    if (pendingTitle !== null) fields.title = pendingTitle;
    if (pendingDescription !== null) fields.description = pendingDescription;
    if (Object.keys(fields).length === 0) return;

    try {
      await updateDiffFields(diff.id, fields);
      const updatedDiff = { ...diff };
      if (pendingTitle !== null) updatedDiff.hook = pendingTitle;
      if (pendingDescription !== null) updatedDiff.description = pendingDescription;
      setDiff(updatedDiff);
      dispatch(updateExternalDiffInList(updatedDiff));
      setPendingTitle(null);
      setPendingDescription(null);
    } catch (error: any) {
      toast({
        title: 'Error saving fields',
        description: error.message || 'Failed to save',
        variant: 'destructive',
      });
      throw error; // re-throw so caller knows save failed
    }
  };

  /**
   * AI generate title or description from event descriptions
   */
  const handleAIGenerate = async (field: 'title' | 'description') => {
    if (!diff || !diff.changes || diff.changes.length === 0) return;

    setAiGeneratingField(field);

    try {
      // Derive company name: "anthropic.api" → "Anthropic API"
      const rawSlug = diff.slug || slug;
      const companyName = rawSlug.split('.').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

      // Build event context
      const validEvents = diff.changes.filter(e => !e.invalid);
      const eventDescriptions = validEvents.map(e => e.description).filter(Boolean).join('; ');

      // Fetch company pricing data for description context
      let pricingContext = '';
      if (field === 'description') {
        try {
          const companyResponse = await fetch(`https://api.pricingsaas.com/companies/${rawSlug}`);
          if (companyResponse.ok) {
            const companyData = await companyResponse.json();

            // Build pricing structure summary
            if (companyData.pages && companyData.pages.length > 0) {
              const page = companyData.pages[0];
              if (page.pricing && page.pricing.plans) {
                const plans = page.pricing.plans;
                const planSummaries = plans.map((plan: any) => {
                  const charges = plan.charges || [];
                  const chargeDetails = charges.map((charge: any) => {
                    const parts = [];
                    if (charge.amount !== null && charge.amount !== undefined) {
                      parts.push(`$${charge.amount}`);
                    }
                    if (charge.unit) parts.push(`per ${charge.unit}`);
                    if (charge.period) parts.push(charge.period);
                    return parts.join(' ');
                  }).join(', ');

                  return `- ${plan.name || 'Unnamed plan'}${chargeDetails ? `: ${chargeDetails}` : ''}`;
                }).join('\n');

                pricingContext = `\n\nCOMPANY PRICING STRUCTURE:\n${planSummaries}\n\nAnalyze the changes in the context of this pricing structure.`;
              }
            }
          }
        } catch (err) {
          console.warn('Failed to fetch company pricing data:', err);
          // Continue without pricing context
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            field: field === 'title' ? 'hook' : 'description',
            customInstructions: field === 'title'
              ? `Company name is "${companyName}". Events: ${eventDescriptions}. Write a short, snappy headline starting with "${companyName}". Most relevant eye-catching detail first after the company name. Example format: "${companyName} adds Opus 4.6 pricing". Keep under 80 characters.`
              : `Here are the event descriptions: ${eventDescriptions}.${pricingContext}\n\nWrite a single newsworthy paragraph about these changes in the context of the company's pricing structure. The paragraph should be about 50% the total length of the event descriptions. Condense each event into fewer words while keeping all key facts. Do not start with the company name. Do not repeat descriptions verbatim — rephrase more concisely. Most important changes first. End with 1-2 sentences of analysis explaining why these changes matter — what they signal about the company's pricing strategy, competitive positioning, or market direction given their overall pricing structure.`,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to generate content');
      }

      const data = await response.json();

      // Enter edit mode with generated text
      if (field === 'title') setPendingTitle(data.text);
      else setPendingDescription(data.text);

      toast({ title: `${field === 'title' ? 'Title' : 'Description'} generated` });
    } catch (error: any) {
      toast({
        title: 'AI generation failed',
        description: error.message || 'Failed to generate content',
        variant: 'destructive',
      });
    } finally {
      setAiGeneratingField(null);
    }
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">Loading diff...</p>
        </div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error || !diff) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="w-full max-w-[1800px] mx-auto px-8 py-12">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-red-900 mb-1">Diff Not Found</h3>
                  <p className="text-sm text-red-800 mb-4">
                    {error || `No diff found for ${slug} in period ${period}`}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate('/diffs2')}
                      variant="outline"
                      size="sm"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Back to List
                    </Button>
                    <Button
                      onClick={loadDiff}
                      variant="outline"
                      size="sm"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  /**
   * Render diff detail
   */
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Bar - Sticky */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="w-full max-w-[1800px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button onClick={handleBackToList} variant="ghost" size="sm">
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to List
              </Button>

              {/* Highlight star toggle */}
              <button
                onClick={handleToggleHighlight}
                className="p-2 rounded-md hover:bg-gray-100 transition-colors"
                title={diff.highlight ? 'Remove highlight' : 'Highlight this diff'}
              >
                <Star
                  className={`h-5 w-5 ${
                    diff.highlight
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-400 hover:text-yellow-400'
                  }`}
                />
              </button>

              {/* Published badge + Eye toggle */}
              {diff.published ? (
                <Badge className="text-xs bg-green-100 text-green-700 border-green-200 pointer-events-none">
                  ● Published
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs bg-gray-50 text-gray-400 border-gray-200 pointer-events-none">
                  ○ Draft
                </Badge>
              )}
              <button
                onClick={handleTogglePublished}
                className="p-2 rounded-md hover:bg-gray-100 transition-colors"
                title={diff.published ? 'Unpublish this diff' : 'Publish this diff'}
              >
                {diff.published ? (
                  <Eye className="h-5 w-5 text-green-600" />
                ) : (
                  <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>

              {/* QA status badge + Redo button */}
              {diff.qa_check ? (
                <>
                  <Badge className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200 pointer-events-none">
                    ✓ QA
                  </Badge>
                  <button
                    onClick={() => setShowRedoQADialog(true)}
                    className="p-2 rounded-md hover:bg-gray-100 transition-colors"
                    title="Reset QA status to pending"
                  >
                    <RotateCcw className="h-4 w-4 text-gray-400 hover:text-orange-500" />
                  </button>
                </>
              ) : (
                <Badge variant="outline" className="text-xs text-muted-foreground pointer-events-none">
                  Pending QA
                </Badge>
              )}
            </div>

            {hasNavigation && (
              <div className="flex items-center gap-4">
                <Button onClick={navigateToPrev} disabled={!hasPrev} variant="outline" size="sm">
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-sm text-gray-600 font-medium">
                  {currentDiffIndex + 1} of {filteredNavigationList.length}
                </span>
                <Button onClick={navigateToNext} disabled={!hasNext} variant="outline" size="sm">
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
                {!diff.published && (
                  <Button
                    onClick={async () => { await handleTogglePublished(); navigateToNext(); }}
                    disabled={!hasNext}
                    variant="outline"
                    size="sm"
                    className="text-green-700 border-green-300 hover:bg-green-50"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Publish & Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Title & Description */}
      <div className="w-full max-w-[1800px] mx-auto px-6 pt-4 pb-0">
        {/* Company logo + slug */}
        <div className="flex items-center gap-2 mb-2">
          {diff.page?.company?.logo_url && (
            <img
              src={diff.page.company.logo_url}
              alt={`${diff.slug} logo`}
              className="h-8 w-8 rounded object-contain bg-white border border-gray-200"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          )}
          <span className="text-base font-semibold text-gray-700">{diff.slug}</span>
        </div>

        {/* Title */}
        {pendingTitle !== null ? (
          <div className="flex items-center gap-2 mb-1">
            <Input
              value={pendingTitle}
              onChange={(e) => setPendingTitle(e.target.value)}
              className="text-lg font-semibold h-9"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveField('title');
                if (e.key === 'Escape') handleCancelField('title');
              }}
            />
            <Button variant="ghost" size="sm" onClick={() => handleSaveField('title')}>
              <Check className="h-4 w-4 text-green-600" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleCancelField('title')}>
              <X className="h-4 w-4 text-gray-400" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1 mb-1">
            <div
              className="cursor-pointer hover:bg-gray-100 rounded px-1 py-0.5 -mx-1 flex-1"
              onClick={handleStartTitleEdit}
            >
              {diff.hook ? (
                <h2 className="text-lg font-semibold text-gray-900">{diff.hook}</h2>
              ) : (
                <h2 className="text-lg italic text-gray-400">Click to add title...</h2>
              )}
            </div>
            {diff.changes && diff.changes.length > 0 && (
              <button
                onClick={() => handleAIGenerate('title')}
                disabled={aiGeneratingField === 'title'}
                className="p-1 rounded hover:bg-purple-50 transition-colors disabled:opacity-50"
                title="Generate title with AI"
              >
                {aiGeneratingField === 'title' ? (
                  <Loader2 className="h-4 w-4 text-purple-400 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 text-purple-400 hover:text-purple-600" />
                )}
              </button>
            )}
          </div>
        )}

        {/* Description */}
        {/* {pendingDescription !== null ? (
          <div className="flex items-start gap-2 mb-2">
            <Textarea
              value={pendingDescription}
              onChange={(e) => setPendingDescription(e.target.value)}
              className="text-sm min-h-[60px]"
              autoFocus={pendingTitle === null}
              onKeyDown={(e) => {
                if (e.key === 'Escape') handleCancelField('description');
              }}
            />
            <div className="flex flex-col gap-1">
              <Button variant="ghost" size="sm" onClick={() => handleSaveField('description')}>
                <Check className="h-4 w-4 text-green-600" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleCancelField('description')}>
                <X className="h-4 w-4 text-gray-400" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-1 mb-2">
            <div
              className="cursor-pointer hover:bg-gray-100 rounded px-1 py-0.5 -mx-1 flex-1"
              onClick={handleStartDescriptionEdit}
            >
              {diff.description ? (
                <p className="text-sm text-gray-600">{diff.description}</p>
              ) : (
                <p className="text-sm italic text-gray-400">Click to add description...</p>
              )}
            </div>
            {diff.changes && diff.changes.length > 0 && (
              <button
                onClick={() => handleAIGenerate('description')}
                disabled={aiGeneratingField === 'description'}
                className="p-1 rounded hover:bg-purple-50 transition-colors disabled:opacity-50 mt-0.5"
                title="Generate description with AI"
              >
                {aiGeneratingField === 'description' ? (
                  <Loader2 className="h-4 w-4 text-purple-400 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 text-purple-400 hover:text-purple-600" />
                )}
              </button>
            )}
          </div>
        )} */}
      </div>

      {/* Inline Diff Viewer */}
      <div className="w-full max-w-[1800px] mx-auto px-6 py-6">
        {diff && diff.changes && diff.changes.length > 0 ? (
          <DiffDetailViewer
            diff={{
              primary_original_url: diff.primary_original_url,
              secondary_original_url: diff.secondary_original_url,
              v_before: diff.v_before,
              v_after: diff.v_after,
              slug: diff.slug,
              page: diff.page,
              company_logo: diff.page?.company?.logo_url,
            }}
            events={diff.changes}
            period={period}
            onDirtyChange={setIsDirty}
            onSave={handleSave}
            onSaveAndNext={handleSaveAndNext}
            onSavePublishAndNext={handleSavePublishAndNext}
            isPublished={!!diff.published}
            defaultEditMode={true}
            showInvalidEvents={showInvalid}
            onInvalidateEvent={handleInvalidateEvent}
            onToggleShowInvalid={() => setShowInvalid(!showInvalid)}
            onOpenWebsite={handleOpenWebsite}
            onOpenPricingPage={handleOpenPricingPage}
            pricingPageUrl={pricingPageUrl}
          />
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-600">No events found for this diff.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Do you want to save before navigating away?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelNavigate}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setIsDirty(false);
                setPendingTitle(null);
                setPendingDescription(null);
                handleConfirmNavigate();
              }}
              variant="outline"
            >
              Discard Changes
            </AlertDialogAction>
            <AlertDialogAction
              onClick={async () => {
                if (hasPendingFieldEdits) await savePendingFields();
                await handleSave();
                handleConfirmNavigate();
              }}
            >
              Save & Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Redo QA Confirmation Dialog */}
      <AlertDialog open={showRedoQADialog} onOpenChange={setShowRedoQADialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset QA Status?</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset the QA check for this diff back to pending.
              The diff will need to be reviewed again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleRedoQA();
                setShowRedoQADialog(false);
              }}
            >
              Reset QA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Diffs2Detail;
